import { IconClose } from '@aws-amplify/ui-react/internal';
import { Flex } from 'antd';
import { useEffect, useMemo } from 'react';

import { ReactComponent as IconDataFlow } from 'assets/images/icons/dataflow.svg';

import { FilterDimension } from 'components/FilterDimension';
import { restrictionDimensionPair } from 'components/PerformanceTable/performanceTypes';
import { IconButton, Tooltip } from 'components/common';
import { IconType } from 'components/common/button/types';

import { useConstantsQuery } from 'modules/apiData/dataApiSlice';
import { usePerformance } from 'modules/performance/usePerformance';

import { useOrganization } from '../../modules/organization/useOrganization';
import {
  DSP_ADFORM,
  DSP_AMAZON,
  DSP_CRITEO,
  DSP_FREEWHEEL,
  DSP_VISTAR,
  DSP_XANDR,
} from '../../utils/constants';

export const FilterDimensionGrid = ({
  selectedDimensions,
  onChangeDimension,
}: {
  selectedDimensions: [string | null, string | null];
  onChangeDimension: (dimensions: [string | null, string | null]) => void;
}) => {
  const { dsp } = useOrganization();
  const { data: constants } = useConstantsQuery(null);
  const { showSecondFilter, pickShowSecondFilter } = usePerformance();
  const allDimensionOptions = useMemo(() => (constants && constants.Dimensions) || {}, [constants]);

  const dimensionOptions = useMemo(
    () =>
      Object.entries(allDimensionOptions)
        .map(([key, value]) => {
          let relatesToDashboard;

          switch (dsp) {
            case DSP_VISTAR:
              relatesToDashboard = value.relatesToVistarDashboard;
              break;
            case DSP_XANDR:
              relatesToDashboard = value.relatesToXandrDashboard;
              break;
            case DSP_CRITEO:
              relatesToDashboard = value.relatesToCriteoDashboard;
              break;
            case DSP_ADFORM:
              relatesToDashboard = value.relatesToAdformDashboard;
              break;
            case DSP_AMAZON:
              relatesToDashboard = value.relatesToAmazonDashboard;
              break;
            case DSP_FREEWHEEL:
              relatesToDashboard = value.relatesToFreewheelDashboard;
              break;
            default:
              relatesToDashboard = value.relatesToXandrDashboard;
          }

          return {
            label: value.shownName,
            value: key,
            relatesToDashboard,
          };
        })
        .filter((el) => el.relatesToDashboard),
    [allDimensionOptions, dsp],
  );

  useEffect(() => {
    const defaultDimension = dimensionOptions.find(({ value }) => {
      let targetValue;

      switch (dsp) {
        case DSP_VISTAR:
          targetValue = 'MEDIA_OWNER';
          break;
        case DSP_CRITEO:
        case DSP_AMAZON:
          targetValue = 'CAMPAIGN';
          break;
        default:
          targetValue = 'PUBLISHER';
          break;
      }

      return value === targetValue;
    });

    if (!selectedDimensions[0] && defaultDimension) {
      onChangeDimension([defaultDimension?.value, selectedDimensions[1]]);
    }
  }, [selectedDimensions, onChangeDimension, dimensionOptions, dsp]);

  const isRestrictedDimension = (firstDimension: string, secondDimension: string) =>
    restrictionDimensionPair[firstDimension as keyof typeof restrictionDimensionPair]?.includes(
      secondDimension,
    );

  const secondDimensionOptions = useMemo(() => {
    return dimensionOptions.map((option) => ({
      ...option,
      disabled: isRestrictedDimension(selectedDimensions[0]!, option.value),
    }));
  }, [selectedDimensions, dimensionOptions]);

  const hasEnabledSecondDimenstions = secondDimensionOptions.some((d) => !d.disabled);

  useEffect(() => {
    if (!hasEnabledSecondDimenstions) {
      pickShowSecondFilter(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEnabledSecondDimenstions]);

  const iconButton = (
    <IconButton
      size="md"
      disabled={!hasEnabledSecondDimenstions}
      variant={showSecondFilter ? 'tertiary' : 'secondary'}
      Icon={showSecondFilter ? (IconClose as IconType) : (IconDataFlow as IconType)}
      onClick={() => {
        if (showSecondFilter && selectedDimensions[1]) {
          onChangeDimension([selectedDimensions[0], null]);
        }
        pickShowSecondFilter(!showSecondFilter);
      }}
    />
  );

  return (
    <Flex gap="small">
      <FilterDimension
        prefixText="1st&nbsp;dimension:"
        allOptions={dimensionOptions}
        selectedOption={selectedDimensions[0]}
        onChange={(value) => {
          const newDimensions: [string | null, string | null] = [
            value,
            isRestrictedDimension(value, selectedDimensions[1]!) ? null : selectedDimensions[1],
          ];

          onChangeDimension(newDimensions);
        }}
      />
      {showSecondFilter && (
        <FilterDimension
          prefixText="2nd&nbsp;dimension:"
          allOptions={secondDimensionOptions}
          selectedOption={selectedDimensions[1]}
          onChange={(value) => {
            const newDimensions: [string | null, string | null] = [selectedDimensions[0], value];

            onChangeDimension(newDimensions);
          }}
        />
      )}
      {(!showSecondFilter && (
        <Tooltip title body="Drill down data">
          {iconButton}
        </Tooltip>
      )) ||
        iconButton}
    </Flex>
  );
};
